import type { AttackDef, Boss, BossString, Player, StrikeProfile } from './types'
import type { BossPoseMotion } from './boss_pose_timeline'
import { stanceBiteReach } from './boss_body_envelope'
import { PLAYER } from './balance'
import { awokenNeckBackPocketPx } from './awoken_neck_back'
import { awokenBiteSpacePocketPx } from './awoken_bite_space'

// 覚醒王の噛みは「口の周り」でだけ当たる (2026-09-26 ユーザー裁定「おすすめどおり懐を安全にして直して」)。
//   2026-09-27: 暴噛・溜め・暴連噛・伸びは「体の前1/3 (高さ全部)」でも当たる (下の AWOKEN_BITE_FRONT_THIRD)。
//   2026-09-27 検収 B-4: 追い噛みの2発目も同じ (口の周り ∪ 前1/3。下の AWOKEN_CHASE_BITE)。
//   旧: 仮の当たり (中心の後ろ100〜前130) が口 (前方約176) と合わず、たてがみ・胸・首の上に立った主人公が噛まれていた。
//   新: 当たりは毎tick、描いている噛みの絵の口の位置から出す (絵が口を閉じた所 = 当たる所)。
//       口より内側 (懐) は噛まれない。体に触れても害は無い (押し出し・立て直しは従来どおり)。
//   調査 = docs/handoffs/2026-09-26-awoken-hit-validity.md
export const AWOKEN_MOUTH_BOSS_ID = 'crowned-king-awoken'

// 口の中心 (顔の光と同じ BOSS_FACE_ANCHOR_IN_ART_BY_POSE の点) の位置。キーはタイムラインの絵の名前。
//   forwardPx = boss.x から前へ、upPx = 絵の回転の軸 (床の6px下) から上へ。どちらもタイムラインの
//   offset/scale/rot を掛ける前の値 (awokenMouthPoint が描画と同じ順で掛ける)。
//   出所 = 本物の drawBossSprite で各姿勢を描き、顔アンカーを写した点から逆算 (2026-09-26・
//   噛み10技の active 全tick と大暴れ第5案の噛みの窓 1050tick で誤差 0.1px 以内)。
//   frenzy_bite2 / exec_bite は本番で描く牙の絵 (*_fangs_0917) の値。絵を差し替えたら測り直す
//   (exec_bite_fangs_1005 は口の中だけ描き直した絵で、口の外は 0917 と同一 = 値はそのまま)。
export const AWOKEN_MOUTH_ART: Readonly<Record<string, { forwardPx: number; upPx: number }>> = {
  bite_contact: { forwardPx: 165.1, upPx: 80.1 },
  bite_step: { forwardPx: 142.4, upPx: 105.5 },
  frenzy_bite2: { forwardPx: 100.2, upPx: 26.8 },
  exec_bite: { forwardPx: 103.9, upPx: 23.9 },
  swoop_bite_0920: { forwardPx: 162.6, upPx: 68.2 },
}
// 絵の回転の軸は床より6px下 (render の足元 y-6 に、sprites の pivot +12)。
const PIVOT_BELOW_FLOOR_PX = 6

// 牙が届く範囲 (口の中心から)。横は後ろ15・前30 (+主人公の半幅15 は当たり側で足す = 口の30px後ろ〜45px先)。
//   縦は主人公の体 (床〜PLAYER_DISPLAY_HEIGHT) から上下 20px まで (口の中心が頭の20px上なら下の牙が頭に届く)。
export const AWOKEN_JAW = { backPx: 15, frontPx: 30, reachPx: 20 } as const

// 口で当たる技。値 = 踏み込みが止まった時に描いている絵の口の前方距離 (= 着地で口が主人公に重なる中心間距離)。
//   暴噛は active 全体が bite_contact (176)。溜め・連噛は2tick目から bite_step (146〜147)。
//   致命は telegraph の寄せ距離 (active 最初の tick の exec_bite = 117)。
//   出所は上と同じ実測 (tests/awoken_bite_mouth.test.ts がタイムラインから引き直して照合する)。
export const AWOKEN_BITE_REST_FORWARD_PX: Readonly<Record<string, number>> = {
  feralBite: 176,
  feralBiteDelayed: 147,
  feralFrenzy: 146,
  feralFrenzyExtend: 146,
  kingExecutionAwoken: 117,
}

export type AwokenMouthPoint = { forwardPx: number; heightPx: number }

/** 描いている絵とタイムラインの変位から口の中心を出す。表に無い絵は undefined。 */
export function awokenMouthPoint(image: string | undefined, motion: BossPoseMotion, altitudePx = 0): AwokenMouthPoint | undefined {
  const art = image ? AWOKEN_MOUTH_ART[image] : undefined
  if (!art) return undefined
  const f = art.forwardPx * motion.scaleX + motion.offsetX
  const u = art.upPx * motion.scaleY - motion.offsetY
  const r = motion.rot * Math.PI / 180, c = Math.cos(r), s = Math.sin(r)
  return { forwardPx: f * c - u * s, heightPx: u * c + f * s + altitudePx - PIVOT_BELOW_FLOOR_PX }
}

export function awokenMouthStrike(forwardPx: number): StrikeProfile {
  return { localFromPx: forwardPx - AWOKEN_JAW.backPx, localToPx: forwardPx + AWOKEN_JAW.frontPx }
}

/** 口の高さが主人公の体 (床〜playerHeight) に牙の届く範囲で重なるか。 */
export function awokenMouthReachesHeight(mouth: AwokenMouthPoint, playerHeight: number): boolean {
  return mouth.heightPx - AWOKEN_JAW.reachPx <= playerHeight && mouth.heightPx + AWOKEN_JAW.reachPx >= 0
}

// ---- 体の前1/3でも当たる (2026-09-27 ユーザー「攻撃の当たり判定は体の前1/3くらいあってもいいと思う。
//      懐に潜るのが簡単すぎる」→ 対象「噛みと連噛み・追い噛み」・高さ「前1/3なら高さも全部」) ----
//   同じ検収で「口の周りだけ当たる・懐では噛まない」の手触りは「このままでよい」= 口の周り (上の区間・口の高さつき) は
//   そのまま残し、前1/3 と合わせた範囲で当たる (今より狭くならない)。懐で噛まない規則も残す (懐の距離だけ縮む)。
//   体 = 立ち絵 (idle) に描いた胴＋頭 (尻尾は含めない)。boss.x から
//     前へ 157 = 頭の先 (bosses.ts の bodyHalfWidthPx と同じ実測。tests/awoken_bite_mouth.test.ts が照合)、
//     後ろへ 108 = 尻の後ろ端 (idle.webp 1441px 幅・表示倍率 186/850。絵の行 350〜500 で胴の後ろ端が x≈1213、
//       中心 720.5 から 492.5 × 0.2188 ≈ 108。後ろ足の先 x≈1276 と尻尾は含めない)。
//   前1/3 = 頭の先から 胴＋頭 265 の 1/3 (≈88.3) 後ろまで = 前方 68.7〜157。高さ = 体の全高 186
//     (= 表示の高さ。たてがみ・首の上・頭の上を含む)。判定は boss.x 基準で固定 (噛みの絵の変位は数px しかない)。
//   追い噛み (feralRushChase の2発目) も 2026-09-27 検収 B-4「他の噛みと揃える」で同じ判定になった (下の AWOKEN_CHASE_BITE)。
export const AWOKEN_BITE_BODY = { frontPx: 157, rearPx: 108, heightPx: 186 } as const
export const AWOKEN_BITE_FRONT_THIRD: StrikeProfile = {
  localFromPx: AWOKEN_BITE_BODY.frontPx - (AWOKEN_BITE_BODY.frontPx + AWOKEN_BITE_BODY.rearPx) / 3,
  localToPx: AWOKEN_BITE_BODY.frontPx,
}
// 前1/3でも当たる噛み = 暴噛・暴噛・溜め・暴連噛・暴連噛・伸び・追い噛みの2発目
//   (致命の暴噛・終と大暴れ第5案の噛みは口の周りだけのまま)。追い噛みの1発目 (突進) は口の判定にならないので、ここに入っていても使われない。
const FRONT_BODY_BITES = ['feralBite', 'feralBiteDelayed', 'feralFrenzy', 'feralFrenzyExtend', 'feralRushChase'] as const
export function awokenBiteUsesFrontBody(attackId: string): boolean {
  return (FRONT_BODY_BITES as readonly string[]).includes(attackId)
}
/** 体の全高 (宙に浮いていればその分上) が主人公の体 (床〜playerHeight) に重なるか。 */
export function awokenFrontBodyReachesHeight(altitudePx: number, playerHeight: number): boolean {
  return altitudePx <= playerHeight && altitudePx + AWOKEN_BITE_BODY.heightPx >= 0
}
/** 噛みの横の区間 (口の周り ∪ 前1/3)。dev H 表示と控えの strike に使う。前1/3 と口の隙間は主人公の幅 (30) より狭い。 */
export function awokenBiteStrike(attackId: string, mouthForwardPx: number, frontBody = false): StrikeProfile {
  const m = awokenMouthStrike(mouthForwardPx)
  if (!frontBody && !awokenBiteUsesFrontBody(attackId)) return m
  return { localFromPx: Math.min(m.localFromPx, AWOKEN_BITE_FRONT_THIRD.localFromPx),
    localToPx: Math.max(m.localToPx, AWOKEN_BITE_FRONT_THIRD.localToPx) }
}

/** この近接ヒットを口で当てるか。DEV の体の比較 (?stanceBody / 獣) は従来の当たりのまま残す。 */
export function isAwokenMouthBite(atk: Pick<AttackDef, 'id' | 'sourceBossId'>): boolean {
  return atk.sourceBossId === AWOKEN_MOUTH_BOSS_ID && atk.id in AWOKEN_BITE_REST_FORWARD_PX
    && stanceBiteReach(atk.id) === undefined
}

// ---- 追い噛み (feralRushChase の2発目) も口の周り ∪ 前1/3 (2026-09-27 検収 B-4「判定: 他の噛みと揃える」) ----
//   旧: 仮の当たり (collisionDraft の中心の後ろ100〜前140) のまま。後ろ側100px まで当たり、口の先 (前方約176〜180) は少し届かなかった。
//   新: 他の噛みと同じく、描いている絵の口の周り (口の高さつき) と体の前1/3 (高さ全部) で当たる。
//   2発目の active で描く絵は全 tick bite_contact (→ 採用絵 bite_contact_closed_0912。牙の差し替えは無い)。口の前方距離は
//   タイムラインの変位 (offset 8→4 / scale 1.05→1.02 / rot 1.5→0.8) を掛けて 171.3〜180.3。最初の tick (接触) は 179.3。
//   変えないもの: 1発目 (突進) の当たり・時間・ダメージ・踏み込み (2発目の踏み込みも従来の式のまま・着地の口で止める式は使わない)・
//   1回だけの向き直り・懐の判断 (連は突進から始まるので懐の噛みの候補外し・間合いの表の対象外)。
export const AWOKEN_CHASE_BITE = { attackId: 'feralRushChase', hitIndex: 1, contactMouthForwardPx: 179 } as const
/** この連撃の段を口で当てるか (追い噛みは2発目だけ・他の噛みは isAwokenMouthBite と同じ)。 */
export function isAwokenMouthBiteHit(atk: Pick<AttackDef, 'id' | 'sourceBossId'>, comboHitIndex: number): boolean {
  if (atk.id === AWOKEN_CHASE_BITE.attackId) {
    return atk.sourceBossId === AWOKEN_MOUTH_BOSS_ID && comboHitIndex === AWOKEN_CHASE_BITE.hitIndex
      && stanceBiteReach(atk.id) === undefined
  }
  return isAwokenMouthBite(atk)
}
/** 口で当てる段の控えの口の前方距離 (spawnMeleeHit の控えの strike 用。実判定は毎tick 絵の口から出す)。 */
export function awokenMouthBiteFallbackForwardPx(attackId: string): number {
  return attackId === AWOKEN_CHASE_BITE.attackId ? AWOKEN_CHASE_BITE.contactMouthForwardPx : AWOKEN_BITE_REST_FORWARD_PX[attackId]!
}

/** 噛みの踏み込みが止まる中心間距離 (口が主人公に重なる所)。対象外は undefined。 */
export function awokenBiteLandingGapPx(boss: Pick<Boss, 'id'>, atk: Pick<AttackDef, 'id' | 'sourceBossId'>): number | undefined {
  if (boss.id !== AWOKEN_MOUTH_BOSS_ID || !isAwokenMouthBite(atk)) return undefined
  return AWOKEN_BITE_REST_FORWARD_PX[atk.id]
}

// ---- 懐では噛まない (2026-09-26 ユーザー裁定「懐では噛まない（推奨）」) ----
//   「懐にいる間は噛みを選ばず、突進や後ろへ下がる動きを選ぶ。モンハンの大型モンスターが足元の相手に
//    位置を取り直すのと同じ考え方。空振りの繰り返しが消え、張り付きだけで休める場所ではなくなる。」
//   懐 = その噛みがどう踏み込んでも当たりが届かない距離。踏み込みは口が重なる所で止まり、懐の相手へは
//   踏み込まない (boss.ts computeLungeTargetX) ので、届く一番近い距離 = 当たりの近い端 − 主人公の半幅。
//   当たりの近い端 = 着地の口 − 牙の後ろ と 体の前1/3 の近い端 (68.7) の近い方。
//   09-26 (口の周りだけ): 暴噛 146 / 溜め 117 / 連噛・伸び 116。
//   09-27 (前1/3 も): 4技とも 53.7 (致命は崩れた相手へ寄せるので対象外)。
//   追い噛みは前1/3 を使うが対象外 (連の入りは突進で、2発目の前に懐の判断を挟まない)。
const POCKET_BITES = ['feralBite', 'feralBiteDelayed', 'feralFrenzy', 'feralFrenzyExtend'] as const
export function awokenBiteMinReachPx(attackId: string): number | undefined {
  if (!(POCKET_BITES as readonly string[]).includes(attackId)) return undefined
  const near = Math.min(AWOKEN_BITE_REST_FORWARD_PX[attackId]! - AWOKEN_JAW.backPx, AWOKEN_BITE_FRONT_THIRD.localFromPx)
  return near - PLAYER.width / 2
}
// 下がって取り直す時の最低限: 届く一番近い距離よりこれだけ外 (歩きの1tickの誤差で懐へ戻らない)。
export const AWOKEN_POCKET_RETREAT_MARGIN_PX = 12

/** この技を今の距離で出すと、口が主人公に届かない (懐)。覚醒王の噛み以外は常に false。 */
export function awokenBiteInPocket(boss: Pick<Boss, 'id' | 'x'>, player: Pick<Player, 'x'>, attackId: string): boolean {
  if (boss.id !== AWOKEN_MOUTH_BOSS_ID || stanceBiteReach(attackId) !== undefined) return false
  const min = awokenBiteMinReachPx(attackId)
  return min !== undefined && Math.abs(player.x - boss.x) < min
}

/**
 * 技を選ぶ時の懐 (これより近ければ、この噛みから始まる連を選ばない)。ふだんは届く一番近い距離と同じ。
 *   DEV ?awokenNeckBack=1 の時だけ、暴噛・溜め噛みは予備動作の顔が主人公に重ならない距離まで広げる (src/awoken_neck_back.ts)。
 *   空振りの後の回転 (awoken_pocket_spin.ts) は awokenBiteInPocket (届くかどうか) を使い、ここは見ない。
 */
export function awokenBiteChoiceMinPx(attackId: string): number | undefined {
  const reach = awokenBiteMinReachPx(attackId)
  if (reach === undefined) return undefined
  return Math.max(reach, awokenNeckBackPocketPx(attackId) ?? reach, awokenBiteSpacePocketPx(attackId) ?? reach)
}

/** 懐で口が届かない噛みから始まる連を外す。覚醒王以外はそのまま返す。 */
export function withoutAwokenPocketBites<T extends Pick<BossString, 'beats'>>(
  boss: Pick<Boss, 'id' | 'x'>, player: Pick<Player, 'x'>, strings: T[],
): T[] {
  if (boss.id !== AWOKEN_MOUTH_BOSS_ID) return strings
  return strings.filter(s => {
    const id = s.beats[0]!.attackId
    const min = awokenBiteChoiceMinPx(id)
    return stanceBiteReach(id) !== undefined || min === undefined || Math.abs(player.x - boss.x) >= min
  })
}

/**
 * 噛みしか出せない時、歩いて下がって止まる間合い。噛みが無ければ undefined。
 *   = 候補の噛みの「届く一番近い距離 + 溜めの間に主人公が歩いて詰められる距離 (溜め × 主人公の歩く速さ)」の最大。
 *   こうしないと、下がった直後の溜めの間に主人公が懐へ歩き戻り、また空振りする (張り付きの実測で確認)。
 *   上限は好みの間合い (capPx = preferredRange 200。これより外へ下がると、待機の「遠いので詰める」歩きが
   数px だけ立ち絵のまま前へ滑る)。そのため溜めの長い噛み (連噛 206・暴噛 204 が要る) は、溜めの間ずっと
   歩いて詰めてくる相手には数px 届かないことがある。下限は届く一番近い距離 + 余白。
 */
export function awokenPocketRetreatGapPx(
  strings: Pick<BossString, 'beats'>[], chargeMsOf: (beat: BossString['beats'][number]) => number, capPx: number,
): number | undefined {
  const bites = strings.map(s => s.beats[0]!).filter(b => awokenBiteChoiceMinPx(b.attackId) !== undefined)
  if (bites.length === 0) return undefined
  const nearest = Math.min(...bites.map(b => awokenBiteChoiceMinPx(b.attackId)!)) + AWOKEN_POCKET_RETREAT_MARGIN_PX
  const walkIn = Math.max(...bites.map(b => awokenBiteChoiceMinPx(b.attackId)! + chargeMsOf(b) * PLAYER.moveSpeed / 1000))
  return Math.max(nearest, Math.min(capPx, walkIn))
}
